import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { topics as hardcodedTopics } from '@/data/videoTopics';

export interface TopicLike {
  id: string;
  title: string;
  description: string;
  videoCount: number;
  videos: Array<{ id: number; title: string; embedCode: string }>;
  isCustom?: boolean;
}

let cache: TopicLike[] | null = null;
const listeners = new Set<(t: TopicLike[]) => void>();

async function loadCustom(): Promise<TopicLike[]> {
  const { data, error } = await supabase
    .from('custom_categories')
    .select('id, title, description, sort_order')
    .order('sort_order', { ascending: true })
    .order('title', { ascending: true });
  if (error) {
    console.error('Failed to load custom categories', error);
    return hardcodedTopics as TopicLike[];
  }
  const custom: TopicLike[] = (data || []).map((c: any) => ({
    id: c.id,
    title: c.title,
    description: c.description || '',
    videoCount: 0,
    videos: [],
    isCustom: true,
  }));
  // Filter out any custom with same id as hardcoded to avoid dupes
  const hardIds = new Set(hardcodedTopics.map((t: any) => t.id));
  const merged = [...(hardcodedTopics as TopicLike[]), ...custom.filter((c) => !hardIds.has(c.id))];
  return merged.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
}

export const refreshAllTopics = async () => {
  cache = await loadCustom();
  listeners.forEach((l) => l(cache!));
  return cache;
};

export const useAllTopics = () => {
  const [topics, setTopics] = useState<TopicLike[]>(cache || (hardcodedTopics as TopicLike[]));
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    listeners.add(setTopics);
    if (!cache) {
      loadCustom().then((t) => {
        cache = t;
        setTopics(t);
        setLoading(false);
        listeners.forEach((l) => l(t));
      });
    } else {
      setLoading(false);
    }
    return () => {
      listeners.delete(setTopics);
    };
  }, []);

  return { topics, loading, refresh: refreshAllTopics };
};
